import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Users, Package, LayoutList, DollarSign, MessageSquare } from "lucide-react";

export default function HowItWorks() {
  return (
    <div className="min-h-screen">
      <MarketingHeader />
      <div className="max-w-7xl mx-auto px-6 py-16">

        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-wide">
            How Fill The Trucks works
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Three simple steps — and your existing freight partners do the rest.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <Card data-testid="card-step-1">
            <CardHeader>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <Badge className="w-fit mb-2">Step 1</Badge>
              <CardTitle>Add your partners</CardTitle>
              <CardDescription>
                Add the brokers and carriers you already work with. They get a free invitation to join and bid on your loads.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Add partners by name, email, and type during signup or anytime from My Partners</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Partners who are already on Fill The Trucks are added instantly</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>New partners receive a free invitation to join and bid — at no cost to them</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card data-testid="card-step-2">
            <CardHeader>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <Badge className="w-fit mb-2">Step 2</Badge>
              <CardTitle>Post your shipment</CardTitle>
              <CardDescription>
                Enter your dims, origin, destination, and pickup date — once. Select which partners to notify and post.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Your selected partners are notified automatically by email with the load details</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Your load also appears on the public board — you may get additional bids from other carriers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>No more repeating the same shipment details over email and phone</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card data-testid="card-step-3">
            <CardHeader>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <LayoutList className="w-6 h-6 text-primary" />
              </div>
              <Badge className="w-fit mb-2">Step 3</Badge>
              <CardTitle>Compare bids and accept</CardTitle>
              <CardDescription>
                See all bids side by side. Ask questions, compare rates and transit times, and accept the best one.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Card and table views so you can compare bids the way that works for you</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Message any bidder directly with questions before accepting</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Accept a bid and the carrier is notified instantly — all other bids are automatically closed</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Why shippers choose Fill The Trucks */}
        <div className="bg-primary/5 rounded-md p-8 mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center tracking-wide">
            Why shippers choose Fill The Trucks
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Everything in one place</h3>
                <p className="text-muted-foreground">
                  No more scattered email threads and phone calls. Every bid, every conversation, every document lives in your Shipment Workspace — organized by load, from quote to delivery confirmation.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Work with partners you already trust</h3>
                <p className="text-muted-foreground">
                  Fill The Trucks is built around your existing relationships — not a cold search board. Your brokers and carriers join for free and bid on your loads. You stay in control of who you work with.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Free to start</h3>
                <p className="text-muted-foreground">
                  The free plan includes 3 shipments per month — no credit card required. Upgrade to $25/month for unlimited posts whenever your volume warrants it.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">0% commission</h3>
                <p className="text-muted-foreground">
                  We charge a simple subscription fee — nothing more. Whatever rate you negotiate with your carrier or broker stays between you. We don't take a cut of your freight.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-card border rounded-md p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to get started?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Start free — no credit card required. Add your partners and post your first shipment in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => window.location.href = "/register"}
              data-testid="button-get-started"
            >
              Get Started Free
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.location.href = "/pricing"}
              data-testid="button-view-pricing"
            >
              View Pricing
            </Button>
          </div>
        </div>

      </div>
      <MarketingFooter />
    </div>
  );
}